/**
 * Notches landing-experience navigation tree — 2026-09-12.
 *
 * Drives the Understand → Evaluate → Execute wizard at /n2. See:
 *   docs/wiki/notches/public-site-docs/Notches_Functional_Specification.md
 *   docs/wiki/notches/public-site-docs/Notches_Design_Specification.md
 *
 * Destinations point at existing app routes. Do not add new routes here —
 * app routing is the source of truth for what actually exists. If a
 * destination is unknown, use `flag: <reason>` and the entrance will
 * present it as a placeholder for review rather than fabricate a link.
 */

export type EntranceNode = {
  /** Stable slug used in the ?path= URL and for focus restoration. */
  id: string;
  /** Serif display label (e.g. "Understand", "SPX"). */
  label: string;
  /** Short sans-serif line under the label on Level 1. */
  subtitle?: string;
  /**
   * Marketing headline shown as the Level-2/3 page h1 when present.
   * Falls back to `label` when absent. Breadcrumb still uses `label`.
   */
  headline?: string;
  /**
   * Short one-sentence microcopy shown as a third line under the label +
   * subtitle on Level 1. Explains what happens in this stage. Kept to a
   * single sentence.
   */
  microcopy?: string;
  /** One-line description shown on the branch header of Levels 2/3. */
  description?: string;
  /** External destination path (routes into the existing app). Leaves only. */
  destination?: string;
  /** Optional CTA label on a leaf ("Explore Premier →"). Falls back to the label. */
  destinationLabel?: string;
  /** Deeper choices. Presence of `children` promotes this node to an intermediate step. */
  children?: EntranceNode[];
  /** Level-1 numeral (01/02/03). */
  numeral?: string;
  /** If set, the node renders as "review — <reason>" and the destination is disabled. */
  flag?: string;
  /**
   * When present, this branch renders a bundle overview (title, price,
   * coverage, benefits, primary CTA) drawn from `plansCatalog`, rather
   * than a child list. Used on the Evaluate branches to route visitors
   * to a plan comparison instead of individual-symbol boards.
   *
   * The key matches a PlanKey; a secondary key can be added to surface
   * a related standalone plan alongside the bundle (e.g. SPX Only on
   * Index & ETF Options).
   */
  bundleKey?: 'premier' | 'index-etf-research' | 'futures-options-research';
  bundleSecondaryKey?: 'spx-only' | 'es-only';
};

export const entranceTree: EntranceNode[] = [
  {
    id: 'understand',
    numeral: '01',
    label: 'Understand',
    subtitle: 'Public Market Research',
    microcopy: 'Start with free market research.',
    headline: 'Put the market in context.',
    description:
      'Explore public research on market conditions, structure, and volatility.',
    children: [
      {
        id: 'briefings',
        label: 'Market Briefings',
        description:
          'Twice-daily reads on regime and positioning. Today plus the last two days are free; the archive and cross-asset analysis are Premier.',
        destination: '/n2/briefings',
      },
      {
        id: 'research',
        label: 'Research Library',
        description: 'Longer-form research on structure and regime.',
        destination: '/n2/research',
      },
      {
        id: 'scorecard',
        label: 'Public Scorecard',
        description: 'Recorded outcomes on published model readings.',
        destination: '/n2/scorecard',
      },
    ],
  },
  {
    id: 'evaluate',
    numeral: '02',
    label: 'Evaluate',
    subtitle: 'Researched Opportunities',
    microcopy: 'Explore researched opportunities and their risks.',
    headline: 'Move from market context to specific researched opportunities.',
    description:
      'Explore opportunities in your chosen markets, with supporting analysis and risk considerations.',
    children: [
      {
        id: 'stocks',
        label: 'Stocks & Equity Options',
        description:
          'Premier — stock research, equity options research, and reporting.',
        bundleKey: 'premier',
      },
      {
        id: 'index-etf',
        label: 'Index & ETF Options',
        description: 'Index & ETF Research — one subscription across SPX, SPY, QQQ, and IWM.',
        bundleKey: 'index-etf-research',
        bundleSecondaryKey: 'spx-only',
      },
      {
        id: 'futures',
        label: 'Futures Options',
        description: 'Futures Options Research — one subscription across ES, MES, GC, and CL.',
        bundleKey: 'futures-options-research',
        bundleSecondaryKey: 'es-only',
      },
    ],
  },
  {
    id: 'execute',
    numeral: '03',
    label: 'Execute',
    subtitle: 'Manual or Automated Trading',
    microcopy: 'Trade through your broker or supported automation.',
    headline: 'Choose how you put the research into practice.',
    description:
      'Trade independently through your own broker, or explore supported Notches automated trading engines. Notches does not provide manual order entry.',
    children: [
      {
        id: 'manual',
        label: 'Manual Trading',
        description:
          'Use the research to inform trades you place independently through your own broker.',
        destination: '/n2/manual-trading',
        destinationLabel: 'How Manual Trading works →',
      },
      {
        id: 'automated',
        label: 'Automated Trading',
        description:
          'Explore dedicated engines for supported systematic trading strategies.',
        destination: '/n2/automated-trading',
        destinationLabel: 'How Automated Trading works →',
      },
    ],
  },
];

/** Resolve a `?path=understand/scorecard` string to the trail of nodes it names. */
export function resolvePath(pathParam: string | null): EntranceNode[] {
  if (!pathParam) return [];
  const segments = pathParam.split('/').filter(Boolean);
  const trail: EntranceNode[] = [];
  let cursor: EntranceNode[] = entranceTree;
  for (const seg of segments) {
    const found = cursor.find((n) => n.id === seg);
    if (!found) break;
    trail.push(found);
    cursor = found.children || [];
  }
  return trail;
}

/** Serialize a trail of nodes back into a `path=` string. */
export function trailToPath(trail: EntranceNode[]): string {
  return trail.map((n) => n.id).join('/');
}
